import cuid from 'cuid';

import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { tokenUtility } from '@fluentsms/auth';

import { SubscriptionClient } from 'subscriptions-transport-ws';
import { cacheConfiguration } from './cacheConfiguration';
import config from './config';
import { inflightRequestsLink } from './inflightRequestLink';

const httpLink = createHttpLink({
  uri: config.uri,
});

const contentfulHttpLink = (spaceId: string) =>
  createHttpLink({
    uri: `https://graphql.contentful.com/content/v1/spaces/${spaceId}/`,
  });

const cleanTypeName = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    const omitTypename = (key: string, value: unknown) => (key === '__typename' ? undefined : value);
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
  }
  return forward(operation).map((data) => {
    return data;
  });
});

/** See buildSplitLink for hacky modification of the url to add an accurate snapshot of the access token */
const subscriptionClient: SubscriptionClient = new SubscriptionClient(config.uriwss, {
  reconnect: true,
  reconnectionAttempts: 3,
  lazy: true,
  connectionParams: () => ({ idToken: tokenUtility.getIdToken() }),
});

const authLink = setContext((_, { headers }) => {
  const accessToken = tokenUtility.getAccessToken();
  const idToken = tokenUtility.getIdToken();

  const isAccessTokenInvalid = !tokenUtility.validateToken(accessToken);
  const isIdTokenInvalid = !tokenUtility.validateToken(idToken);

  if (isAccessTokenInvalid || isIdTokenInvalid) {
    window.location.href = '/logout';
    return;
  }

  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clientTimeOffset = new Date().getTimezoneOffset();

  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : '',
      'correlation-object': JSON.stringify({
        idToken: idToken ? idToken : '',
        correlationId: cuid(),
        clientTimeZone,
        clientTimeOffset,
      }),
    },
  };
});

const contentfulAuthLink = (contentDeliveryApiKey: string) =>
  setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: contentDeliveryApiKey ? `Bearer ${contentDeliveryApiKey}` : '',
      },
    };
  });

const cache = new InMemoryCache(cacheConfiguration);

const buildSplitLink = (): ApolloLink => {
  const accessToken = tokenUtility.getAccessToken();
  const idToken = tokenUtility.getIdToken();
  const areTokensValid = tokenUtility.validateToken(accessToken) && tokenUtility.validateToken(idToken);

  if (idToken && accessToken && areTokensValid) {
    // 6/7 - This is hacky and there's probably a better solution... however, subscriptions-transport-ws is a dead project so no changes will come.
    // We should migrate when we have time. Additionally, we should pass the token in the connectionParams or a header, not the URL.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    console.log('buildSplitLink WS');
    (subscriptionClient as any).url = config.uriwss + '?' + 'token=' + accessToken;
    const wsLink = new WebSocketLink(subscriptionClient);

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query) as { operation: string; name?: { value: string } };
        return (
          definition.operation === 'subscription' ||
          definition.name?.value === 'SendMessageMutation' ||
          definition.name?.value === 'AccessEntityMutation' ||
          definition.name?.value === 'CloseEntityMutation' ||
          definition.name?.value === 'GetActiveUsers'
        );
      },
      wsLink,
      httpLink,
    );

    console.info('Valid tokens found. Using splitlink');
    return splitLink;
  } else {
    if (!process.env.JEST_WORKER_ID) {
      console.warn('Valid tokens not found. Using httpLink');
    }
    return httpLink;
  }
};

export const client = new ApolloClient({
  link: ApolloLink.from([inflightRequestsLink, cleanTypeName, authLink, buildSplitLink()]),
  cache,
  connectToDevTools: true,
});

export const setApolloLink = () => {
  client.setLink(ApolloLink.from([inflightRequestsLink, cleanTypeName, authLink, buildSplitLink()]));
};

export const contentfulClient = (spaceId: string, contentApiKey: string) =>
  new ApolloClient({
    link: ApolloLink.from([
      inflightRequestsLink,
      cleanTypeName,
      contentfulAuthLink(contentApiKey),
      contentfulHttpLink(spaceId),
    ]),
    cache,
    connectToDevTools: true,
  });

export default client;
